import { FormatType } from '@revelio/iso-utility';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { PositionedItem } from '../../components/PageConfigProvider/types/item';
import { FilterId } from '../../filters/types/filters';
import {
  demandSupplyRatioRankInfoText,
  marketTightnessInfoText,
  timeToFillRankInfoText,
  wagePressureRankInfoText,
} from '../market-conditions-info-text';

const items: PositionedItem[] = [
  {
    id: 'e280213c-9495-468d-b3b1-1b8aa347965c',
    title: 'Market Tightness',
    isTitleManuallySet: true,
    position: { x: 0, y: 0, width: 30, height: 12 },
    info: { text: marketTightnessInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "market_tightness"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '1f9d4b51-b654-4497-b1fd-9817773ab3a1',
    title: 'Demand/Supply Ratio',
    isTitleManuallySet: true,
    position: { x: 0, y: 12, width: 10, height: 11 },
    info: { text: demandSupplyRatioRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "demand_supply_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '2d9351c7-7425-4b50-a281-bb792bba6fe7',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 12, width: 10, height: 11 },
    info: { text: wagePressureRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "wage_pressure_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: 'cfe21886-2e8f-4899-ae54-c709eaca6c8f',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 12, width: 10, height: 11 },
    info: { text: timeToFillRankInfoText },
    plot: {
      chartType: 'gauge-v2',
      format: FormatType.PERCENTAGE,
      gaugeV2: {
        value: '{{VALUE "time_to_fill_percentile"}}',
        minLabel: 'Favors Employers',
        maxLabel: 'Favors Employees',
      },
    },
  },
  {
    id: '0396188b-8ddf-4b76-bfb4-5752eb2f3064',
    title: 'Demand/Supply',
    position: { x: 0, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['demand_supply'],
      format: FormatType.RATIO,
    },
  },
  {
    id: '07edfb55-9865-4432-aea5-f673e4419558',
    title: 'Wage Pressure',
    isTitleManuallySet: true,
    position: { x: 10, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['wage_pressure'],
      format: FormatType.PERCENTAGE,
    },
  },
  {
    id: 'f320b178-99d1-4707-9abd-51183c07f6cb',
    title: 'Time to Fill',
    isTitleManuallySet: true,
    position: { x: 20, y: 23, width: 10, height: 13 },
    plot: {
      chartType: 'line',
      defaultMetricOptions: ['time_to_fill'],
      format: FormatType.INTEGER,
    },
  },
  {
    id: 'c0081e50-0c12-444f-8a01-45143b252216',
    title: 'Postings by Company',
    isTitleManuallySet: true,
    position: { x: 30, y: 0, width: 18, height: 36 },
    plot: {
      chartType: 'grouped-bar',
      secondaryDimension: 'company',
      defaultMetricOptions: ['postings_active'],
      format: FormatType.DECIMAL,
    },
  },
];

const tabContent: TabContent = {
  id: RevelioPageId.SKILL_MARKET_CONDITIONS,
  primaryFilter: { id: FilterId.CUSTOM_SKILL },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.COMPANY,
    FilterId.INDUSTRY,
    FilterId.GENDER,
    FilterId.GEOGRAPHY,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  items,
};

export const skillMarketConditions: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.SKILL_MARKET_CONDITIONS,
  revelioPageId: RevelioPageId.SKILL_MARKET_CONDITIONS,
  name: 'Market Conditions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: RevelioPageId.SKILL_MARKET_CONDITIONS,
      label: 'Market Conditions',
      isActive: true,
      tabContent,
    },
  ],
};
